import { useCallback, useLayoutEffect, useRef } from 'react';

const useSimpleScrollPreservation = () => {
  const contentRef = useRef(null);
  const scrollPositionRef = useRef({ top: 0, left: 0 });
  const lastScrollHeightRef = useRef(0);
  const lastUserScrollRef = useRef(Date.now());
  const restoringRef = useRef(false);

  const findParentScrollContainer = useCallback((element) => {
    if (!element) return null;
    let current = element.parentElement;
    while (current && current !== document.body) {
      const style = window.getComputedStyle(current);
      const hasScroll =
        style.overflow === 'auto' ||
        style.overflow === 'scroll' ||
        style.overflowY === 'auto' ||
        style.overflowY === 'scroll';

      if (hasScroll) {
        return current;
      }

      current = current.parentElement;
    }
    return null;
  }, []);

  useLayoutEffect(() => {
    const element = contentRef.current;
    if (!element) {
      return;
    }

    const scrollContainer = findParentScrollContainer(element);
    if (!scrollContainer) {
      return;
    }

    // Initialize scroll height
    lastScrollHeightRef.current = scrollContainer.scrollHeight;

    // Capture scroll position on scroll
    const handleScroll = () => {
      if (restoringRef.current) {
        return;
      }
      scrollPositionRef.current = {
        top: scrollContainer.scrollTop,
        left: scrollContainer.scrollLeft
      };
      lastUserScrollRef.current = Date.now();
    };

    // Helper to restore scroll position if not user-initiated
    const restoreScroll = () => {
      const sinceUserScroll = Date.now() - lastUserScrollRef.current;
      if (sinceUserScroll > 100) {
        const { top, left } = scrollPositionRef.current;
        restoringRef.current = true;
        scrollContainer.scrollTop = top;
        scrollContainer.scrollLeft = left;
        setTimeout(() => {
          restoringRef.current = false;
        }, 0);
      }
      lastScrollHeightRef.current = scrollContainer.scrollHeight;
    };

    // Helper to capture scroll position
    const captureScroll = () => {
      scrollPositionRef.current = {
        top: scrollContainer.scrollTop,
        left: scrollContainer.scrollLeft
      };
    };

    // Observe DOM mutations
    const mutationObserver = new MutationObserver((mutations) => {
      restoringRef.current = true;
      captureScroll();
      requestAnimationFrame(() => {
        restoreScroll();
        setTimeout(() => {
          restoringRef.current = false;
        }, 0);
      });
    });

    // Observe size changes as a fallback
    let resizeObserver;
    if (window.ResizeObserver) {
      resizeObserver = new window.ResizeObserver(() => {
        requestAnimationFrame(restoreScroll);
      });
      resizeObserver.observe(scrollContainer);
    }

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    mutationObserver.observe(element, {
      childList: true,
      subtree: true,
      characterData: true
    });

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
      mutationObserver.disconnect();
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [findParentScrollContainer]);

  return contentRef;
};

// Simple wrapper component
const SimpleScrollPreserver = ({ children }) => {
  const contentRef = useSimpleScrollPreservation();
  return <div ref={contentRef}>{children}</div>;
};

export { useSimpleScrollPreservation, SimpleScrollPreserver };
